import _ from 'lodash';

'use strict';

class _User {
  constructor() {
    this.id = '';
    this.fname = '';
    this.lname = '';
    this.email = '';
    this.role = '';
    this.$promise = undefined;
  }
}

export const AuthService = [
  '$location',
  '$http',
  '$cookies',
  '$q',
  'appConfig',
  'Util',
  'UserSvc',
  'ToastrSvc',
  'NavbarSvc',
  function(
    $location,
    $http,
    $cookies,
    $q,
    appConfig,
    Util,
    UserSvc,
    ToastrSvc,
    NavbarSvc
  ) {
    var safeCb = Util.safeCb;
    var currentUser = new _User();
    var userRoles = appConfig.userRoles || [];
    /**
     * Check if userRole is >= role
     * @param {String} userRole - role of current user
     * @param {String} role - role to check against
     */
    var hasRole = function(userRole, role) {
      return userRoles.indexOf(userRole) >= userRoles.indexOf(role);
    };
    /**
     * Set cookie token with expiration
     * @param {String} token - cookie token
     */
    var setCookie = function(token) {
      var expires = new Date();
      expires.setDate(expires.getDate() + 14);
      $cookies.put('token', token, {expires});
    };

    if($cookies.get('token') && $location.path() !== '/logout') {
      currentUser = UserSvc.getAuth().$promise
        .then(function(user) {
          NavbarSvc.setNavbarUser(user);
          currentUser = user;
          return user;
        });
    }
    var AuthSvc = {
      /**
       * Authenticate user and save token
       *
       * @param  {Object}   user     - login info
       * @param  {Function} callback - function(error, user)
       * @return {Promise}
       */
      login({ email, password }, callback) {
        return $http.post('/auth/local', {
          email,
          password
        })
          .then(res => {
            setCookie(res.data.token);
            currentUser = UserSvc.getAuth();
            return currentUser.$promise;
          })
          .then(function(user) {
            NavbarSvc.setNavbarUser(user);
            safeCb(callback)(null, user);
            return user;
          })
          .catch(err => {
            AuthSvc.logout(false);
            safeCb(callback)(err.data);
            return $q.reject(err.data);
          });
      },

      /**
       * Delete access token and user info
       */
      logout(showToastr) {
        if(typeof showToastr === 'undefined') { showToastr = true; }
        $cookies.remove('token');
        currentUser = new _User();
        NavbarSvc.setNavbarUser();
        if(showToastr) { ToastrSvc.success('You have been logged out'); }
      },

      /**
       * Create a new user
       *
       * @param  {Object}   user     - user info
       * @param  {Function} callback - function(error, user)
       * @return {Promise}
       */
      createUser(user, callback) {
        return UserSvc.save(user, function(data) {
          setCookie(data.token);
          currentUser = UserSvc.getAuth().$promise.then(function(u) {
            NavbarSvc.setNavbarUser(u);
            return u;
          });
          return safeCb(callback)(null, user);
        }, function(err) {
          AuthSvc.logout(false);
          return safeCb(callback)(err);
        })
          .$promise;
      },

      /**
       * Change password
       *
       * @param  {String}   oldPassword
       * @param  {String}   newPassword
       * @param  {Function} callback    - function(error, user)
       * @return {Promise}
       */
      changePassword(oldPassword, newPassword, callback) {
        return UserSvc.changePassword({
          id: currentUser.id
        }, {
          oldPassword,
          newPassword
        }, function() {
          return safeCb(callback)(null);
        }, function(err) {
          return safeCb(callback)(err);
        })
          .$promise;
      },

      /**
       * Request new password be emailed
       *
       * @param  {String}   email
       * @param  {Function} callback    - function(error, data)
       * @return {Promise}
       */
      forgotPassword(email, callback) {
        return UserSvc.requestReset({
          email
        }, function(data) {
          safeCb(callback)(null, data);
          return data;
        }, function(err) {
          safeCb(callback)(err.data);
          return $q.reject(err.data);
        })
          .$promise;
      },

      /**
       * Validate if provided reset token is valid
       *
       * @param  {String}   token
       * @param  {Function} callback    - function(error, data)
       * @return {Promise}
       */
      checkToken(token, callback) {
        return UserSvc.checkToken({token},
          function(data) {
            safeCb(callback)(null, data);
            return data;
          }, function(err) {
            safeCb(callback)(err.data);
            return $q.reject(err.data);
          })
          .$promise;
      },

      /**
       * Request new password be emailed
       *
       * @param  {String}   token
       * @param  {String}   password
       * @param  {Function} callback    - function(error, data)
       * @return {Promise}
       */
      setPassword(token, password, callback) {
        return UserSvc.setPassword({
          token, password
        },
        function(data) {
          setCookie(data.token);
          currentUser = UserSvc.getAuth();
          return currentUser.$promise;
        }, function(err) {
          safeCb(callback)(err.data);
          return $q.reject(err.data);
        })
          .$promise;
      },

      /**
       * Gets all available info on a user
       *
       * @param  {Function} [callback] - function(user)
       * @return {Promise}
       */
      getCurrentUser(callback) {
        var value = _.get(currentUser, '$promise')
          ? currentUser.$promise : currentUser;

        return $q.when(value)
          .then(user => {
            safeCb(callback)(user);
            return user;
          }, () => {
            safeCb(callback)({});
            return {};
          });
      },

      /**
       * Gets all available info on a user
       *
       * @return {Object}
       */
      getCurrentUserSync() {
        return currentUser;
      },

      /**
       * Check if a user is logged in
       *
       * @param  {Function} [callback] - function(is)
       * @return {Promise}
       */
      isLoggedIn(callback) {
        return AuthSvc.getCurrentUser(undefined)
          .then(user => {
            let is = _.get(user, 'role');
            safeCb(callback)(is);
            return is;
          });
      },

      /**
       * Check if a user is logged in
       *
       * @return {Bool}
       */
      isLoggedInSync() {
        return !!_.get(currentUser, 'role');
      },

      /**
       * Check if a user has a specified role or higher
       *
       * @param  {String}     role     - the role to check against
       * @param  {Function} [callback] - function(has)
       * @return {Promise}
       */
      hasRole(role, callback) {
        return AuthSvc.getCurrentUser(undefined)
          .then(user => {
            let has = hasRole(_.get(user, 'role'), role);

            safeCb(callback)(has);
            return has;
          });
      },

      /**
       * Check if a user has a specified role or higher
       *
       * @param  {String} role - the role to check against
       * @return {Bool}
       */
      hasRoleSync(role) {
        return hasRole(_.get(currentUser, 'role'), role);
      },

      /**
       * Check if a user is an admin
       *   (synchronous|asynchronous)
       *
       * @param  {Function|*} callback - optional, function(is)
       * @return {Bool|Promise}
       */
      isAdmin(...args) {
        // eslint-disable-next-line prefer-spread
        return AuthSvc.hasRole.apply(
          AuthSvc,
          [].concat(['admin'], args)
        );
      },

      /**
       * Check if a user is an admin
       *
       * @return {Bool}
       */
      isAdminSync() {
        return AuthSvc.hasRoleSync('admin');
      },

      /**
       * Get auth token
       *
       * @return {String} - a token string used for authenticating
       */
      getToken() {
        return $cookies.get('token');
      }
    };

    return AuthSvc;
  }
];
